import React, { useState, useContext, useRef } from 'react';

import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import {
    Loading,
    TableCustom,
    MessageModal,
    SectionHeading,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import { ManagedContext } from '../../context/custom';
import { useLista, useNav } from '../../components/hooks'
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { PERMISSIONS } from '../../consts/permissions';
import { usePermissions } from '../../components/hooks/usePermissions';

function CertificadosApremiosView() {

    //#region Hooks
    const navigate = useNav()
    const { showMessageModal } = useContext(ManagedContext)
    
    const [state, setState] = useState({
        loading: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const searchRef = useRef();
    const tableRef = useRef();

    const [, getRowLista ] = useLista({
        listas: ['TipoDocumento','EstadoCertificadoApremio'],
        onLoaded: (listas, isSuccess, error) => {
          if (!isSuccess) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'EstadoCertificadoApremio_TipoDocumento',
          timeout: 0
        }
    });

    const [tableVisible, setTableVisible] = useState(false);

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.APREMIO_EDIT);

    //#endregion

    const getDescTipoDocumento = (id) => {
        const row = getRowLista('TipoDocumento', id);
        return (row) ? row.nombre : '';
    }

    const getDescEstadoCertificadoApremio = (id) => {
        const row = getRowLista('EstadoCertificadoApremio', id);
        return (row) ? row.nombre : '';
    }

    //definiciones
    const tableColumns = [
        { Header: 'N° Certificado', accessor: 'numero', width: '10%' },
        { Header: 'N° Apremio', accessor: 'numeroApremio', width: '10%' },
        { Header: 'Estado certificado', Cell: (data) => getDescEstadoCertificadoApremio(data.value), accessor: 'idEstadoCertificadoApremio', width: '10%' },
        { Header: 'Cuenta', accessor: 'numeroCuenta', width: '20%' },
        { Header: 'Titular', Cell: (data) => 
                                    `${data.row.original.nombreContribuyente} 
                                    (${getDescTipoDocumento(data.row.original.idTipoDocumentoContribuyente)}
                                    ${data.row.original.numeroDocumentoContribuyente})`
                                    , width: '24%' },
        { Header: 'Habilitado', accessor: 'pagoHabilitado', width: '8%', Cell: ({ value }) => value ? 'Sí' : 'No' },
        { Header: 'VEP', accessor: 'numeroRecibo', width: '8%' },
    ];

    //handles
    const avmr = {
        onAdd: () => {
            handleClickAdd()
        },
        onView: ({ id }) => {
            navigateWithFilters(`/certificado-apremio/${OPERATION_MODE.VIEW}/${id}`)
        },
        onModify: ({ id }) => {
            navigateWithFilters(`/certificado-apremio/${OPERATION_MODE.EDIT}/${id}`)
        },
        onRemove: ({ id }) => {
            const certificado = state.list.find(x => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar el certificado ${certificado.numero}?`,
                onConfirm: () => RemoveCertificadoApremio(id),
            })
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setState(prevState => ({...prevState, dataTagger: { showModal: true, idEntidad: id, disabled }}))
        },
    }

    const handleClickAdd = () => {
        navigateWithFilters(`/certificado-apremio-nuevo`)
    }

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }

    const SearchCertificadosApremiosWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.CERTIFICADO_APREMIO,
        buildParamsUrl: (filters) => {
            const formatDate = (date) => {
                if (!date) return '';
                const d = new Date(date);
                return d.toISOString().split('T')[0];
            };
            return `/filter?` +
                `idApremio=${filters.idApremio}&` +
                `idCuenta=${filters.idCuenta}&` +
                `idEstadoCertificadoApremio=${filters.idEstadoCertificadoApremio}&` +
                `numero=${encodeURIComponent(filters.numero)}&` +
                `fechaCertificadoDesde=${encodeURIComponent(formatDate(filters.fechaCertificadoDesde))}&` +
                `fechaCertificadoHasta=${encodeURIComponent(formatDate(filters.fechaCertificadoHasta))}`;
        },
        callbackNoSuccess,
        callbackError
    });

    const { getCurrentFilters, restoredPageIndex, restoredGlobalFilter } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchCertificadosApremiosWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters)

    //funciones
    function SearchCertificadosApremio() {
        SearchCertificadosApremiosWithSavedFilters(getCurrentFilters());
    }

    function RemoveCertificadoApremio(id) {
        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((id) => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                SearchCertificadosApremio();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.CERTIFICADO_APREMIO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const dataTaggerRegistro = state.list.find(x => x.id === state.dataTagger.idEntidad)?.nombreContribuyente ?? ''

    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.dataTagger.showModal &&
            <DataTaggerModalApi
                title="Información adicional de certificado apremio"
                registro={dataTaggerRegistro}
                entidad="CertificadoApremio"
                idEntidad={state.dataTagger.idEntidad}
                disabled={false}
                onConfirm={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
            />
        }

        <SectionHeading 
            titles={[
                { title: 'Legales' },
                { title: 'Certificados' },
            ]}
            onAddClick={canEdit ? handleClickAdd : null}
            tableVisible={tableVisible}
        />

        <section className='section-accordion'>

            <WrapperAdvancedSearch
                ref={searchRef}
                filters={[
                    { 
                        title: 'Apremio', 
                        field: 'idApremio', 
                        type: 'entity',
                        entidad: 'Apremio',
                        onFormat: (row) => (row && row.id) ? `Número: ${row.numero} - Caratula: ${row.caratula}` : '',
                        onLabelFormat: (row) => (row && row.id) ? `Número: ${row.numero} - Caratula: ${row.caratula}` : '',
                        columns: [
                            { Header: 'Número', accessor: 'numero', width: '25%' },
                            { Header: 'Carátula', accessor: 'caratula', width: '70%' }
                        ],
                        size: 4 
                    },
                    { title: 'Cuenta', field: 'idCuenta', type: 'cuenta', size: 4 },
                    { title: 'Estado certificado', field: 'idEstadoCertificadoApremio', type: 'selectLista', lista: 'EstadoCertificadoApremio', size: 2 },
                    { title: 'Número certificado', field: 'numero', type: 'string', size: 2, searchOnEnter: true },
                    { title: 'Fecha certificado desde', field: 'fechaCertificadoDesde', type: 'date', size: 2 },
                    { title: 'Fecha certificado hasta', field: 'fechaCertificadoHasta', type: 'date', minValueField: 'fechaCertificadoDesde', size: 2 }
                ]}
                onSearch={SearchCertificadosApremio}
            />

            {tableVisible && (
                <TableCustom
                    ref={tableRef}
                    className={'TableCustomBase m-top-20'}
                    columns={tableColumns}
                    data={state.list}
                    avmr={avmr}
                    editCode={PERMISSIONS.CERTIFICADO_APREMIO_EDIT}
                    restoredPageIndex={restoredPageIndex}
                    defaultGlobalFilter={restoredGlobalFilter}
                    messageEmpty="No se encontraron resultados"
                />
            )}

        </section>
        
    </>
    )
}

export default CertificadosApremiosView;

